import { useEffect, useState, useMemo } from 'react';
import { useAuthStore } from '@stores/auth.store';
import { subscribeTenantDocument } from '@services/registry';
import { subscribePlans } from '@services/firebase/planService';

/**
 * Resolves the effective limit for a given module key by checking:
 *   1. Tenant-level moduleOverrides (highest priority)
 *   2. Plan-level module config (fallback)
 *
 * Returns:
 *   limit       — numeric cap (null when the module has no limit type)
 *   exceeded    — true when currentCount >= limit
 *   remaining   — how many more items can be created
 *   loading     — true while tenant/plan data is loading
 *   limitLabel  — human-readable label like "200 / 300 used"
 */
export function useModuleLimit({ moduleKey, currentCount = 0 }) {
    const tenantId = useAuthStore((s) => s.user?.tenantId);
    const [tenantDoc, setTenantDoc] = useState(null);
    const [plans, setPlans] = useState([]);
    const [tenantLoading, setTenantLoading] = useState(true);
    const [plansLoading, setPlansLoading] = useState(true);

    // Subscribe to tenant document
    useEffect(() => {
        if (!tenantId) {
            setTenantLoading(false);
            return;
        }
        const unsub = subscribeTenantDocument({
            tenantId,
            onNext: (doc) => {
                setTenantDoc(doc);
                setTenantLoading(false);
            },
            onError: (err) => {
                console.error('[useModuleLimit] tenant sub error:', err);
                setTenantLoading(false);
            },
        });
        return () => unsub();
    }, [tenantId]);

    // Subscribe to plans
    useEffect(() => {
        const unsub = subscribePlans(
            (rows) => {
                setPlans(rows);
                setPlansLoading(false);
            },
            (err) => {
                console.error('[useModuleLimit] plans sub error:', err);
                setPlansLoading(false);
            }
        );
        return () => unsub();
    }, []);

    const result = useMemo(() => {
        const loading = tenantLoading || plansLoading;
        if (loading || !tenantDoc) {
            return { limit: null, exceeded: false, remaining: Infinity, loading, limitLabel: '' };
        }

        // Resolve the current plan
        const currentPlan = plans.find(
            (p) => p.name === tenantDoc.plan || p.code === tenantDoc.plan || p.id === tenantDoc.plan
        ) || null;

        // Get plan-level module config
        const planMod = currentPlan?.modules?.find((m) => m.key === moduleKey) || null;
        const planLimit = planMod?.limit ?? null;

        // Get tenant override
        const override = tenantDoc?.moduleOverrides?.[moduleKey] || {};
        const effectiveLimit = override.limit !== undefined ? override.limit : planLimit;

        if (effectiveLimit === null || effectiveLimit === undefined) {
            return { limit: null, exceeded: false, remaining: Infinity, loading: false, limitLabel: '' };
        }

        const numLimit = Number(effectiveLimit);
        const count = Number(currentCount) || 0;
        const exceeded = count >= numLimit;
        const remaining = Math.max(0, numLimit - count);
        const limitLabel = `${count} / ${numLimit} used`;

        return { limit: numLimit, exceeded, remaining, loading: false, limitLabel };
    }, [tenantDoc, plans, tenantLoading, plansLoading, moduleKey, currentCount]);

    return result;
}
